import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { REMEMBER_SECONDS } from '../../components/gameslib/difficulty'
import { useGameSettings } from '../../components/gameslib/gameSettings'
import { useTutorial } from '../../components/gameslib/useTutorial'
import {
  canSlide,
  findEmpty,
  isSolved,
  planShuffle,
  PUZZLE_GRID,
  randomPuzzleId,
  renderPuzzleImage,
  scorePuzzle,
  shuffleStepsFor,
  slide,
  solvedBoard,
} from '../../components/gameslib/puzzle'
import { sfxSlide, sfxSnap, sfxSubmit, sfxWhoosh, unlockAudioSync } from '../../components/gameslib/audio'
import { Countdown, MemoryTimer } from '../gamesshared/Countdown'
import { GameShell, IntroScreen, PlayerBanner } from '../gamesshared/IntroScreen'
import { PassDevice, PlayerSetup, WinnerScreen } from '../gamesshared/Multiplayer'
import { RoundResult, TotalScreen } from '../gamesshared/RoundResult'
import { TutorialCoach } from '../gamesshared/TutorialCoach'

function puzzleCoach(step) {
  if (step === 'watch') return { message: 'Memorize the photo', gesture: 'watch', anchor: 'center' }
  if (step === 'slide') return { message: 'Slide a tile', gesture: 'slide', anchor: 'center' }
  if (step === 'done') return { message: 'Nice — you’re ready to play', gesture: 'tap', anchor: 'submit' }
  return null
}

const SLIDE_MS = 220
const JUMBLE_SLIDE_MS = 72
const JUMBLE_STEP_MS = 78
const SNAP_THRESHOLD = 0.28

function makeRoundIds(count) {
  const ids = []
  for (let i = 0; i < count; i++) ids.push(randomPuzzleId(ids.slice(-3)))
  return ids
}

function cellPos(index, n) {
  return { row: Math.floor(index / n), col: index % n }
}

function formatTime(ms) {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

function PuzzleBoard({
  board,
  n,
  imageUrl,
  interactive,
  onMoved,
  celebrating,
  slideMs = SLIDE_MS,
  showEmpty = true,
  coachActive = false,
  onCoachTile = null,
}) {
  const boardRef = useRef(null)
  const hintTileRef = useRef(null)
  const nextRef = useRef(null)
  const [drag, setDrag] = useState(null)
  const [animating, setAnimating] = useState(false)
  const [cell, setCell] = useState(0)
  const animTimer = useRef(0)
  const boardSnap = useRef(board)
  boardSnap.current = board

  const empty = findEmpty(board)
  const gap = 3

  const hintIndex = useMemo(() => {
    if (!coachActive) return -1
    return board.findIndex((tileId, i) => tileId !== null && canSlide(board, i, n))
  }, [board, n, coachActive])

  const slideAxis = useMemo(() => {
    if (hintIndex < 0 || empty < 0) return null
    const a = cellPos(hintIndex, n)
    const b = cellPos(empty, n)
    return a.row === b.row ? 'x' : 'y'
  }, [hintIndex, empty, n])

  useLayoutEffect(() => {
    if (!onCoachTile) return
    if (!coachActive || hintIndex < 0) {
      onCoachTile(null, null)
      return
    }
    onCoachTile(hintTileRef.current, slideAxis)
  }, [coachActive, hintIndex, slideAxis, cell, board, onCoachTile])

  useEffect(() => {
    const el = boardRef.current
    if (!el) return undefined
    const measure = () => {
      const size = el.clientWidth
      setCell((size - gap * (n - 1)) / n)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [n])

  const tileStyle = (tileId, boardIndex) => {
    const { row, col } = cellPos(boardIndex, n)
    const home = cellPos(tileId, n)
    let x = col * (cell + gap)
    let y = row * (cell + gap)

    if (drag && drag.tileId === tileId) {
      x += drag.dx
      y += drag.dy
    }

    return {
      width: cell,
      height: cell,
      transform: `translate3d(${x}px, ${y}px, 0)`,
      backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
      backgroundSize: `${n * 100}% ${n * 100}%`,
      backgroundPosition: `${(home.col / (n - 1)) * 100}% ${(home.row / (n - 1)) * 100}%`,
      transition:
        drag?.tileId === tileId
          ? 'none'
          : `transform ${slideMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
      zIndex: drag?.tileId === tileId ? 4 : 1,
    }
  }

  const clearAnim = () => {
    window.clearTimeout(animTimer.current)
    animTimer.current = window.setTimeout(() => setAnimating(false), slideMs + 20)
  }

  const commitMove = useCallback(
    (tileIndex) => {
      if (!interactive || animating) return false
      const next = slide(boardSnap.current, tileIndex, n)
      if (!next) return false
      setAnimating(true)
      void sfxSlide()
      onMoved(next)
      window.setTimeout(() => void sfxSnap(), slideMs * 0.72)
      clearAnim()
      return true
    },
    [animating, interactive, n, onMoved, slideMs],
  )

  const onPointerDown = (e, tileId, boardIndex) => {
    if (!interactive || animating || !canSlide(board, boardIndex, n)) return
    unlockAudioSync()
    e.currentTarget.setPointerCapture(e.pointerId)
    const emptyPos = cellPos(empty, n)
    const tilePos = cellPos(boardIndex, n)
    const axis = emptyPos.row === tilePos.row ? 'x' : 'y'
    const dir =
      axis === 'x'
        ? emptyPos.col > tilePos.col
          ? 1
          : -1
        : emptyPos.row > tilePos.row
          ? 1
          : -1
    setDrag({
      tileId,
      boardIndex,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      dx: 0,
      dy: 0,
      axis,
      dir,
    })
  }

  const onPointerMove = (e) => {
    if (!drag || e.pointerId !== drag.pointerId) return
    const max = cell + gap
    let dx = e.clientX - drag.startX
    let dy = e.clientY - drag.startY
    if (drag.axis === 'x') {
      dy = 0
      dx = Math.max(0, Math.min(max, dx * drag.dir)) * drag.dir
    } else {
      dx = 0
      dy = Math.max(0, Math.min(max, dy * drag.dir)) * drag.dir
    }
    setDrag((d) => (d ? { ...d, dx, dy } : d))
  }

  const onPointerUp = (e) => {
    if (!drag || e.pointerId !== drag.pointerId) return
    const max = cell + gap
    const traveled = Math.abs(drag.axis === 'x' ? drag.dx : drag.dy)
    const fromIndex = drag.boardIndex
    setDrag(null)
    if (traveled / max >= SNAP_THRESHOLD || traveled < 6) {
      commitMove(fromIndex)
    }
  }

  useEffect(() => () => window.clearTimeout(animTimer.current), [])

  useEffect(() => {
    if (!interactive) return undefined
    const onKey = (e) => {
      if (animating) return
      const emptyIdx = findEmpty(boardSnap.current)
      const { row, col } = cellPos(emptyIdx, n)
      let target = -1
      if (e.key === 'ArrowUp' && row < n - 1) target = emptyIdx + n
      if (e.key === 'ArrowDown' && row > 0) target = emptyIdx - n
      if (e.key === 'ArrowLeft' && col < n - 1) target = emptyIdx + 1
      if (e.key === 'ArrowRight' && col > 0) target = emptyIdx - 1
      if (target >= 0) {
        e.preventDefault()
        commitMove(target)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [animating, commitMove, interactive, n])

  return (
    <div
      ref={boardRef}
      className={`puzzle-board${celebrating ? ' is-solved' : ''}`}
      style={{ '--puzzle-n': n }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {cell > 0 &&
        board.map((tileId, boardIndex) => {
          if (tileId === null) {
            if (!showEmpty) return null
            const { row, col } = cellPos(boardIndex, n)
            const x = col * (cell + gap)
            const y = row * (cell + gap)
            return (
              <div
                key="empty"
                className="puzzle-tile puzzle-tile--empty"
                style={{
                  width: cell,
                  height: cell,
                  transform: `translate3d(${x}px, ${y}px, 0)`,
                }}
                aria-hidden
              />
            )
          }
          const movable = interactive && canSlide(board, boardIndex, n) && !animating
          const isHint = coachActive && boardIndex === hintIndex
          return (
            <button
              key={tileId}
              ref={isHint ? hintTileRef : undefined}
              type="button"
              className={`puzzle-tile${movable ? ' is-movable' : ''}${drag?.tileId === tileId ? ' is-dragging' : ''}${isHint ? ' is-tutor-hint' : ''}`}
              style={tileStyle(tileId, boardIndex)}
              aria-label={`Tile ${tileId + 1}`}
              disabled={!interactive}
              onPointerDown={(e) => onPointerDown(e, tileId, boardIndex)}
            />
          )
        })}
    </div>
  )
}

export function PuzzleGame() {
  const { rounds, setRounds, setModeLabel } = useGameSettings()
  const tutorial = useTutorial('puzzle')
  const [phase, setPhase] = useState('intro')
  const [difficulty, setDifficulty] = useState('easy')
  const [round, setRound] = useState(0)
  const [puzzleIds, setPuzzleIds] = useState([])
  const [imageUrl, setImageUrl] = useState(null)
  const [board, setBoard] = useState([])
  const [shuffleMoves, setShuffleMoves] = useState(0)
  const [moves, setMoves] = useState(0)
  const [scores, setScores] = useState([])
  const [lastScore, setLastScore] = useState(0)
  const [celebrating, setCelebrating] = useState(false)
  const [jumbling, setJumbling] = useState(false)
  const [peeking, setPeeking] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [scrambleSeed, setScrambleSeed] = useState(0)
  const [coachTileEl, setCoachTileEl] = useState(null)
  const [coachSlideAxis, setCoachSlideAxis] = useState(null)
  const playStartRef = useRef(0)
  const jumbleKeyRef = useRef(0)
  const tutorialRef = useRef(tutorial)
  const nextRef = useRef(null)

  const [playMode, setPlayMode] = useState('solo')
  const [players, setPlayers] = useState([])
  const [playerIndex, setPlayerIndex] = useState(0)

  tutorialRef.current = tutorial
  const playDifficulty = tutorial.active ? 'easy' : difficulty
  const n = PUZZLE_GRID[playDifficulty] || 3
  const rememberS = tutorial.active ? Math.max(REMEMBER_SECONDS.easy, 3.5) : REMEMBER_SECONDS[difficulty]
  const playRounds = tutorial.active ? 1 : rounds
  const currentPlayer = players[playerIndex]
  const puzzleId = puzzleIds[round]

  useEffect(() => {
    setModeLabel(playMode === 'multi' ? 'multi' : 'solo')
  }, [playMode, setModeLabel])

  const resetTurn = (shared = puzzleIds) => {
    setScores([])
    setRound(0)
    setMoves(0)
    setLastScore(0)
    setCelebrating(false)
    setJumbling(false)
    setPeeking(false)
    setElapsedMs(0)
    setBoard([])
    setImageUrl(null)
    if (shared?.length) setPuzzleIds(shared)
  }

  const beginPlay = (asTutorial) => {
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
    if (asTutorial) tutorial.start('watch')
    else if (tutorial.active) tutorial.finish()
    const ids = makeRoundIds(asTutorial ? 1 : rounds)
    setPuzzleIds(ids)
    resetTurn(ids)
    setPhase('countdown')
  }

  const startSolo = () => beginPlay(tutorial.needsTutorial)
  const startTeach = () => beginPlay(true)

  const exitTutorial = () => {
    tutorial.skip()
    setPhase('intro')
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
  }

  const openMulti = () => {
    if (tutorial.active) tutorial.finish()
    setPlayMode('multi')
    setPhase('setup')
  }

  const confirmPlayers = (list) => {
    if (tutorial.active) tutorial.finish()
    const ids = makeRoundIds(rounds)
    setPuzzleIds(ids)
    setPlayers(list.map((p) => ({ ...p, scores: [] })))
    setPlayerIndex(0)
    resetTurn(ids)
    setPhase('pass')
  }

  const beginPlayerTurn = () => {
    resetTurn(puzzleIds)
    setPhase('countdown')
  }

  const finishPlayerTurn = (finalScores) => {
    if (playMode === 'solo') {
      setPhase('total')
      return
    }
    const updated = players.map((p, i) => (i === playerIndex ? { ...p, scores: finalScores } : p))
    setPlayers(updated)
    if (playerIndex >= players.length - 1) {
      setPhase('winner')
    } else {
      setPlayerIndex((i) => i + 1)
      setPhase('pass')
    }
  }

  const onCountdownDone = useCallback(() => {
    if (tutorial.active) tutorial.go('watch')
    setPhase('stimulus')
  }, [tutorial])

  const onStimulusDone = useCallback(() => setPhase('play'), [])

  // Load image for this round (stimulus / play / result)
  useEffect(() => {
    if (phase !== 'stimulus' && phase !== 'play' && phase !== 'result') return undefined
    if (!puzzleId) return undefined
    let cancelled = false
    void renderPuzzleImage(puzzleId, 720).then((url) => {
      if (!cancelled && url) setImageUrl(url)
    })
    return () => {
      cancelled = true
    }
  }, [phase, puzzleId, round])

  useEffect(() => {
    if (phase === 'stimulus') void sfxWhoosh()
  }, [phase, round])

  // After scramble ends, teach sliding
  useEffect(() => {
    const tut = tutorialRef.current
    if (!tut.active || phase !== 'play' || jumbling) return
    if (tut.step === 'watch') tut.go('slide')
  }, [phase, jumbling])

  const runJumble = useCallback(
    (steps) => {
      jumbleKeyRef.current += 1
      const key = jumbleKeyRef.current
      const { frames, shuffleMoves: sm } = planShuffle(n, steps)
      setBoard(solvedBoard(n))
      setShuffleMoves(sm)
      setMoves(0)
      setCelebrating(false)
      setJumbling(true)
      setElapsedMs(0)
      playStartRef.current = 0

      let i = 1
      let timer = 0

      const tick = () => {
        if (jumbleKeyRef.current !== key) return
        if (i >= frames.length) {
          setBoard(frames[frames.length - 1])
          setJumbling(false)
          playStartRef.current = performance.now()
          void sfxSnap()
          return
        }
        setBoard(frames[i])
        void sfxSlide()
        i += 1
        timer = window.setTimeout(tick, JUMBLE_STEP_MS)
      }

      timer = window.setTimeout(tick, 280)
      return () => window.clearTimeout(timer)
    },
    [n],
  )

  // Scramble when play starts, reshuffle, or image changes
  useEffect(() => {
    if (phase !== 'play' || !imageUrl) return undefined
    const steps = shuffleStepsFor(n, playDifficulty)
    return runJumble(steps)
  }, [phase, round, n, playDifficulty, scrambleSeed, imageUrl, runJumble])

  // Elapsed timer while playing
  useEffect(() => {
    if (phase !== 'play' || jumbling || celebrating) return undefined
    if (!playStartRef.current) playStartRef.current = performance.now()
    let raf = 0
    const loop = () => {
      setElapsedMs(performance.now() - playStartRef.current)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [phase, jumbling, celebrating])

  const reshuffle = () => {
    if (jumbling || celebrating) return
    setScrambleSeed((s) => s + 1)
  }

  const changeImage = () => {
    if (jumbling || celebrating) return
    const nextId = randomPuzzleId([puzzleId])
    setPuzzleIds((ids) => ids.map((id, i) => (i === round ? nextId : id)))
    setImageUrl(null)
    setScrambleSeed((s) => s + 1)
  }

  const onMoved = useCallback((nextBoard) => {
    setBoard(nextBoard)
    const tut = tutorialRef.current
    setMoves((m) => {
      const nextMoves = m + 1

      // Demo ends after a single slide — no need to solve the full board
      if (tut.active && (tut.step === 'slide' || tut.step === 'keep')) {
        tut.go('done')
        window.setTimeout(() => {
          tutorialRef.current.finish()
          setPhase('intro')
        }, 1100)
        return nextMoves
      }

      if (isSolved(nextBoard)) {
        setCelebrating(true)
        const sm = shuffleMoves
        const grid = n
        window.setTimeout(() => {
          void sfxSubmit()
          const s = scorePuzzle(nextMoves, grid, sm)
          setLastScore(s)
          setScores((prev) => [...prev, s])
          setPhase('result')
        }, 520)
      }
      return nextMoves
    })
  }, [n, shuffleMoves])

  const next = () => {
    if (tutorial.active) {
      tutorial.finish()
      setPhase('intro')
      return
    }
    if (round + 1 >= playRounds) {
      const finalScores = scores.length >= playRounds ? scores : [...scores, lastScore]
      finishPlayerTurn(finalScores)
    } else {
      setRound((r) => r + 1)
      setMoves(0)
      setCelebrating(false)
      setJumbling(false)
      setImageUrl(null)
      setPhase('countdown')
    }
  }

  const backHome = () => {
    if (tutorial.active) tutorial.finish()
    setPhase('intro')
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
  }

  const previewBoard = useMemo(() => {
    const cells = Array.from({ length: n * n - 1 }, (_, i) => i)
    cells.push(null)
    return cells
  }, [n])

  const onCoachTile = useCallback((el, axis) => {
    setCoachTileEl(el)
    setCoachSlideAxis(axis)
  }, [])

  const roundWord = rounds === 1 ? 'puzzle' : 'puzzles'
  const coach =
    tutorial.active && !(phase === 'play' && jumbling) ? puzzleCoach(tutorial.step) : null
  const coachOnTile = coach && tutorial.step === 'slide'
  const coachTarget = tutorial.step === 'done' ? nextRef : null

  return (
    <GameShell>
      {phase === 'intro' && (
        <IntroScreen
          title="puzzle"
          blurb={`Slide the tiles to rebuild the image.\nWe'll show you ${rounds} ${roundWord} — memorize the picture, then solve the scramble. Fewer moves score higher.`}
          onSolo={startSolo}
          onMulti={openMulti}
          onTeach={startTeach}
          difficulty={difficulty}
          onDifficulty={setDifficulty}
          rounds={rounds}
          onRounds={setRounds}
        />
      )}

      {phase === 'setup' && <PlayerSetup onConfirm={confirmPlayers} onBack={() => setPhase('intro')} />}
      {phase === 'pass' && currentPlayer && (
        <PassDevice player={currentPlayer} onReady={beginPlayerTurn} />
      )}
      {phase === 'winner' && <WinnerScreen players={players} onAgain={backHome} />}

      {(phase === 'countdown' || phase === 'stimulus' || phase === 'play') && (
        <div className="screen puzzle-screen">
          {playMode === 'multi' && currentPlayer && <PlayerBanner name={currentPlayer.name} />}
          {phase === 'countdown' && <Countdown seconds={3} onDone={onCountdownDone} />}
          {phase === 'stimulus' && (
            <div className="puzzle-stage puzzle-stage--stimulus">
              <MemoryTimer seconds={rememberS} onDone={onStimulusDone} />
              <p className="puzzle-meta">Memorize</p>
              <div className="puzzle-frame puzzle-frame--preview">
                {imageUrl ? (
                  <img src={imageUrl} alt="" className="puzzle-preview" />
                ) : (
                  <div className="puzzle-preview puzzle-preview--loading" />
                )}
              </div>
              {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
            </div>
          )}
          {phase === 'play' && (
            <div className="puzzle-stage puzzle-stage--play">
              <div className="puzzle-stats">
                <div className="puzzle-stat">
                  <strong>{jumbling ? '—' : moves}</strong>
                  <span>Moves</span>
                </div>
                <div className="puzzle-stat">
                  <strong>{jumbling ? '0:00' : formatTime(elapsedMs)}</strong>
                  <span>Time</span>
                </div>
              </div>
              <div className={`puzzle-frame puzzle-frame--play${jumbling ? ' is-jumbling' : ''}`}>
                {imageUrl && board.length === n * n ? (
                  <>
                    <PuzzleBoard
                      board={board}
                      n={n}
                      imageUrl={imageUrl}
                      interactive={!celebrating && !jumbling && !peeking && tutorial.step !== 'done'}
                      onMoved={onMoved}
                      celebrating={celebrating}
                      slideMs={jumbling ? JUMBLE_SLIDE_MS : SLIDE_MS}
                      coachActive={!!coachOnTile}
                      onCoachTile={onCoachTile}
                    />
                    {peeking && imageUrl && (
                      <div className="puzzle-peek" aria-hidden>
                        <img src={imageUrl} alt="" className="puzzle-preview" />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="puzzle-preview puzzle-preview--loading" />
                )}
              </div>
              <div className="puzzle-actions">
                <button
                  type="button"
                  className="puzzle-btn puzzle-btn--primary"
                  disabled={jumbling || celebrating || tutorial.active}
                  onClick={reshuffle}
                >
                  Shuffle
                </button>
                <button
                  type="button"
                  className="puzzle-btn puzzle-btn--ghost"
                  disabled={jumbling || celebrating || !imageUrl}
                  onPointerDown={() => setPeeking(true)}
                  onPointerUp={() => setPeeking(false)}
                  onPointerLeave={() => setPeeking(false)}
                  onPointerCancel={() => setPeeking(false)}
                >
                  Hold to peek
                </button>
                <button
                  type="button"
                  className="puzzle-btn puzzle-btn--ghost"
                  disabled={jumbling || celebrating || tutorial.active}
                  onClick={changeImage}
                >
                  Change image
                </button>
              </div>
              <p className="puzzle-hint">
                {jumbling ? 'Watch the scramble' : 'Slide tiles into the gap'}
              </p>
              {coach && (
                <TutorialCoach
                  {...coach}
                  onSkip={exitTutorial}
                  targetEl={coachOnTile ? coachTileEl : null}
                  slideAxis={coachOnTile ? coachSlideAxis : null}
                />
              )}
            </div>
          )}
        </div>
      )}

      {phase === 'result' && (
        <div className="puzzle-result">
          <RoundResult
            score={lastScore}
            round={tutorial.active ? 1 : round + 1}
            totalRounds={playRounds}
            onNext={next}
            nextButtonRef={nextRef}
            scoreColor="#F29F00"
          >
            <div className="puzzle-compare">
              <div className="puzzle-frame puzzle-frame--mini">
                {imageUrl && (
                  <PuzzleBoard
                    board={previewBoard}
                    n={n}
                    imageUrl={imageUrl}
                    interactive={false}
                    onMoved={() => {}}
                    celebrating
                  />
                )}
              </div>
              <p className="puzzle-compare__meta">
                Solved in <strong>{moves}</strong> moves
                <span>{n}×{n}</span>
              </p>
            </div>
          </RoundResult>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}
      {phase === 'total' && <TotalScreen scores={scores} onReplay={backHome} />}
    </GameShell>
  )
}
